import { useEffect, useRef, useState } from "react";

import { toast } from "@jti/core-components/components/Toaster";
import { createRequestId, useMessageBus } from "@jti/fe-message-bus";
import type { IBundleOptionInput } from "@jti/magento";

import { useTranslations } from "../../providers/useTranslations";

export type AddProduct = {
  isRunning: boolean;
  addProductToCart: (quantity?: number, currentBundles?: IBundleOptionInput[], isConsentGiven?: boolean) => void;
};

export const useAddProduct = (
  sku: string,
  bundle?: IBundleOptionInput[],
  isFreeTrial = false,
  onErrorCallback: () => void = () => undefined
): AddProduct => {
  const { __ } = useTranslations();
  const [isRunning, setIsRunning] = useState(false);
  const isRunningRef = useRef(false);
  const messageBus = useMessageBus();
  const unsubscribeRef = useRef<() => void>(() => undefined);

  const setRunning = (value: boolean) => {
    isRunningRef.current = value;
    setIsRunning(value);
  };

  useEffect(() => () => unsubscribeRef.current(), []);

  const subscribeToCartAddResult = (requestId: string) => {
    const successToken = messageBus.subscribe({
      channel: "success.cart.add",
      callback: ({ payload: { requestId: replyRequestId } }) => {
        if (replyRequestId !== requestId) {
          return;
        }

        unsubscribeRef.current();
        toast.success(__("productdetails.addproduct.success"));

        setRunning(false);

        const headerElement = window.document.querySelector(".header__container");
        if (headerElement) {
          headerElement.classList.remove("header__container--hide");
        }
      },
    });

    const failToken = messageBus.subscribe({
      channel: "fail.cart.add",
      callback: ({ payload: { message, requestId: replyRequestId } }) => {
        if (replyRequestId !== requestId) {
          return;
        }

        unsubscribeRef.current();
        toast.error(__(message));

        setRunning(false);

        if (onErrorCallback) {
          onErrorCallback();
        }
      },
    });

    unsubscribeRef.current = () => {
      messageBus.unsubscribeToken(successToken);
      messageBus.unsubscribeToken(failToken);
    };
  };

  const addProductToCart: AddProduct["addProductToCart"] = (quantity = 1, currentBundles, isConsentGiven) => {
    if (isRunningRef.current) {
      return;
    }

    const requestId = createRequestId();

    setRunning(true);
    subscribeToCartAddResult(requestId);

    messageBus.publish(
      "cart.add",
      {
        payload: {
          sku,
          bundle: currentBundles ?? (bundle as IBundleOptionInput[]),
          quantity,
          isFreeTrial,
          isConsentGiven,
          requestId,
        },
      },
      { sticky: true }
    );
  };

  return {
    isRunning,
    addProductToCart,
  };
};
